/**
 * Background Upload Context
 *
 * Permite subir archivos en segundo plano sin bloquear la UI.
 * Los uploads continúan aunque el usuario navegue a otras páginas.
 *
 * Supports multiple upload types:
 * - 'orbit-images': ZIP with 360° images for OrbitViews
 * - 'virtual-tour': ZIP with 3DVista virtual tour
 * - 'bulk-media': ZIP with media files for bulk upload
 */

import { createContext, useContext, useState, useCallback, useRef, ReactNode } from 'react';
import { API_URL, TOKEN_KEYS, UPLOAD_CONFIG } from '@/config/api.config';
import { toast } from 'sonner';

export type UploadStatus = 'pending' | 'uploading' | 'processing' | 'completed' | 'error';

/**
 * Upload types supported by the background upload system
 */
export type UploadType = 'orbit-images' | 'virtual-tour' | 'bulk-media';

/**
 * Configuration for each upload type
 */
const UPLOAD_TYPE_CONFIG: Record<UploadType, {
  presignedUrlEndpoint: string;
  processEndpoint: string;
  successMessage: (name: string, count: number) => string;
  processingMessage: (name: string) => string;
}> = {
  'orbit-images': {
    presignedUrlEndpoint: '/projects/orbit-views/get-upload-url/',
    processEndpoint: '/projects/orbit-views/process-uploaded-zip/',
    successMessage: (name, count) => `Carpeta "${name}" subida (${count} imagenes)`,
    processingMessage: (name) => `Procesando imagenes de "${name}"...`,
  },
  'virtual-tour': {
    presignedUrlEndpoint: '/projects/virtual-tours/get-upload-url/',
    processEndpoint: '/projects/virtual-tours/process-uploaded/',
    successMessage: (name, count) => `Tour "${name}" subido (${count} panoramas)`,
    processingMessage: (name) => `Procesando tour "${name}"...`,
  },
  'bulk-media': {
    presignedUrlEndpoint: '/projects/media/bulk-upload-url/',
    processEndpoint: '/projects/media/apply-bulk/',
    successMessage: (_name, count) => `Media subida (${count} archivos)`,
    processingMessage: (name) => `Procesando media "${name}"...`,
  },
};

export interface BackgroundUpload {
  id: string;
  type: UploadType;
  projectId: string;
  projectName?: string;
  folderName: string;
  fileName: string;
  fileSize: number;
  status: UploadStatus;
  progress: number;
  stage: 'uploading' | 'processing';
  startedAt: Date;
  completedAt?: Date;
  error?: string;
  /** Additional data sent to the presigned URL endpoint */
  metadata?: Record<string, unknown>;
  result?: {
    folder_name?: string;
    s3_path?: string;
    root_files?: number;
    low_files?: number;
    detected_extension?: string;
    // Virtual tour specific
    tour_id?: string;
    public_url?: string;
    panorama_count?: number;
    total_files?: number;
    // Bulk media specific
    matched_count?: number;
    error_count?: number;
  };
}

interface BackgroundUploadContextData {
  /** Lista de uploads activos y completados */
  uploads: BackgroundUpload[];

  /** Número de uploads activos (uploading o processing) */
  activeCount: number;

  /**
   * Iniciar un nuevo upload en background
   *
   * @param type - Type of upload ('orbit-images' | 'virtual-tour' | 'bulk-media')
   * @param projectId - Project ID
   * @param projectName - Project name (for display)
   * @param fileName - Name for the upload (folder name or tour name)
   * @param file - File to upload (ZIP)
   * @param metadata - Additional data to send with the presigned URL request
   * @param onComplete - Callback when upload completes
   * @returns Upload ID
   */
  startUpload: (
    type: UploadType,
    projectId: string,
    projectName: string,
    fileName: string,
    file: File,
    metadata?: Record<string, unknown>,
    onComplete?: (result: BackgroundUpload) => void
  ) => string;

  /**
   * @deprecated Use startUpload with type parameter instead
   * Legacy method for backward compatibility with orbit-images
   */
  startOrbitImagesUpload: (
    projectId: string,
    projectName: string,
    folderName: string,
    zipFile: File,
    onComplete?: (result: BackgroundUpload) => void
  ) => string;

  /** Cancelar un upload (solo si está en uploading) */
  cancelUpload: (uploadId: string) => void;

  /** Eliminar un upload completado o con error de la lista */
  removeUpload: (uploadId: string) => void;

  /** Limpiar todos los uploads completados */
  clearCompleted: () => void;

  /** Si hay uploads activos */
  hasActiveUploads: boolean;

  /** Mostrar/ocultar panel de notificaciones */
  isNotificationPanelOpen: boolean;
  setNotificationPanelOpen: (open: boolean) => void;
}

const BackgroundUploadContext = createContext<BackgroundUploadContextData | undefined>(undefined);

interface BackgroundUploadProviderProps {
  children: ReactNode;
}

export function BackgroundUploadProvider({ children }: BackgroundUploadProviderProps) {
  const [uploads, setUploads] = useState<BackgroundUpload[]>([]);
  const [isNotificationPanelOpen, setNotificationPanelOpen] = useState(false);

  // Ref para almacenar XHR activos (para poder cancelarlos)
  const activeXHRs = useRef<Map<string, XMLHttpRequest>>(new Map());
  // Ref para callbacks de completado
  const completionCallbacks = useRef<Map<string, (result: BackgroundUpload) => void>>(new Map());

  const generateId = () => `upload-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  const updateUpload = useCallback((uploadId: string, updates: Partial<BackgroundUpload>) => {
    setUploads(prev => prev.map(u =>
      u.id === uploadId ? { ...u, ...updates } : u
    ));
  }, []);

  const startUpload = useCallback((
    type: UploadType,
    projectId: string,
    projectName: string,
    fileName: string,
    file: File,
    metadata?: Record<string, unknown>,
    onComplete?: (result: BackgroundUpload) => void
  ): string => {
    const uploadId = generateId();
    const config = UPLOAD_TYPE_CONFIG[type];

    // Guardar callback si existe
    if (onComplete) {
      completionCallbacks.current.set(uploadId, onComplete);
    }

    const newUpload: BackgroundUpload = {
      id: uploadId,
      type,
      projectId,
      projectName,
      folderName: fileName,
      fileName: file.name,
      fileSize: file.size,
      status: 'uploading',
      progress: 0,
      stage: 'uploading',
      startedAt: new Date(),
      metadata,
    };

    setUploads(prev => [newUpload, ...prev]);
    setNotificationPanelOpen(true);

    // Usar toast de sonner para mostrar progreso
    const toastId = `upload-${uploadId}`;
    toast.loading(`Subiendo "${fileName}"... 0%`, { id: toastId });

    // Ejecutar upload de forma asíncrona
    (async () => {
      try {
        // Use Firebase token first, fallback to JWT token
        const firebaseToken = localStorage.getItem('firebase_token');
        const jwtToken = localStorage.getItem(TOKEN_KEYS.ACCESS);
        const accessToken = firebaseToken || jwtToken;

        if (!accessToken) {
          throw new Error('No authentication token found');
        }

        // Step 1: Get presigned URL (endpoint varies by type)
        const presignedRequestBody: Record<string, unknown> = {
          project_id: projectId,
          ...(type === 'orbit-images' ? { folder_name: fileName } : { name: fileName }),
          ...metadata,
        };

        const presignedResponse = await fetch(`${API_URL}${config.presignedUrlEndpoint}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify(presignedRequestBody),
        }).then(async res => {
          if (!res.ok) {
            const errorData = await res.json().catch(() => ({}));
            const errorMsg = errorData.error || errorData.detail || `HTTP ${res.status}: ${res.statusText}`;
            throw new Error(`Failed to get upload URL: ${errorMsg}`);
          }
          return res.json();
        });

        // Step 2: Upload to S3 with XHR for progress tracking
        await new Promise<void>((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          activeXHRs.current.set(uploadId, xhr);

          xhr.upload.addEventListener('progress', (event) => {
            if (event.lengthComputable) {
              const progress = Math.round((event.loaded / event.total) * 100);
              updateUpload(uploadId, { progress });
              toast.loading(`Subiendo "${fileName}"... ${progress}%`, { id: toastId });
            }
          });

          xhr.addEventListener('load', () => {
            activeXHRs.current.delete(uploadId);
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve();
            } else {
              reject(new Error(`S3 upload failed with status ${xhr.status}`));
            }
          });

          xhr.addEventListener('error', () => {
            activeXHRs.current.delete(uploadId);
            reject(new Error('Network error during upload'));
          });

          xhr.addEventListener('abort', () => {
            activeXHRs.current.delete(uploadId);
            reject(new Error('Upload cancelled'));
          });

          xhr.open('PUT', presignedResponse.upload_url);
          xhr.setRequestHeader('Content-Type', 'application/zip');
          xhr.send(file);
        });

        // Step 3: Process on backend (endpoint and body vary by type)
        updateUpload(uploadId, {
          status: 'processing',
          stage: 'processing',
          progress: 100
        });
        toast.loading(config.processingMessage(fileName), { id: toastId });

        const processRequestBody: Record<string, unknown> = {
          project_id: projectId,
          s3_key: presignedResponse.s3_key,
          ...(type === 'orbit-images' ? { folder_name: fileName } : {}),
          ...(type === 'virtual-tour' ? {
            tour_id: presignedResponse.tour_id,
            async: true  // Use async processing for virtual tours
          } : {}),
          ...metadata,
        };

        let processResponse = await fetch(`${API_URL}${config.processEndpoint}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify(processRequestBody),
        }).then(res => {
          if (!res.ok) throw new Error('Failed to process file');
          return res.json();
        });

        // For virtual tours with async processing, poll for completion
        if (type === 'virtual-tour' && processResponse.status === 'processing') {
          const tourId = presignedResponse.tour_id;
          const maxPolls = UPLOAD_CONFIG.MAX_POLLS;
          const pollIntervalMs = UPLOAD_CONFIG.POLL_INTERVAL_MS;
          let polls = 0;

          while (polls < maxPolls) {
            await new Promise(resolve => setTimeout(resolve, pollIntervalMs));
            polls++;

            const statusResponse = await fetch(`${API_URL}/projects/virtual-tours/${tourId}/`, {
              headers: { 'Authorization': `Bearer ${accessToken}` },
            }).then(res => res.json());

            if (statusResponse.status === 'active') {
              processResponse = {
                success: true,
                tour_id: tourId,
                public_url: statusResponse.public_url,
                panorama_count: statusResponse.panorama_count,
                total_files: statusResponse.total_files,
              };
              break;
            } else if (statusResponse.status === 'error') {
              throw new Error(statusResponse.error_message || 'Tour processing failed');
            }

            // Update toast with elapsed time
            const elapsedSeconds = polls * (pollIntervalMs / 1000);
            const elapsedMinutes = Math.floor(elapsedSeconds / 60);
            const remainingSeconds = Math.floor(elapsedSeconds % 60);
            const timeStr = elapsedMinutes > 0 ? `${elapsedMinutes}m ${remainingSeconds}s` : `${remainingSeconds}s`;
            toast.loading(`${config.processingMessage(fileName)} (${timeStr})`, { id: toastId });
          }

          if (polls >= maxPolls) {
            throw new Error('Tour processing timeout - please check status later');
          }
        }

        // Success! Build result based on type
        const result: BackgroundUpload['result'] = {};

        // Common fields
        if (processResponse.folder_name) result.folder_name = processResponse.folder_name;
        if (processResponse.s3_path) result.s3_path = processResponse.s3_path;

        // Orbit images specific
        if (type === 'orbit-images') {
          result.root_files = processResponse.root_files;
          result.low_files = processResponse.low_files;
          result.detected_extension = processResponse.detected_extension;
        }

        // Virtual tour specific
        if (type === 'virtual-tour') {
          result.tour_id = processResponse.tour_id;
          result.public_url = processResponse.public_url;
          result.panorama_count = processResponse.panorama_count;
          result.total_files = processResponse.total_files;
        }

        // Bulk media specific
        if (type === 'bulk-media') {
          result.matched_count = processResponse.matched_count;
          result.error_count = processResponse.error_count;
        }

        const completedUpload: BackgroundUpload = {
          ...newUpload,
          status: 'completed',
          progress: 100,
          completedAt: new Date(),
          result,
        };

        updateUpload(uploadId, completedUpload);

        // Mostrar toast de éxito
        const successCount = result.root_files || result.panorama_count || result.matched_count || 0;
        toast.success(config.successMessage(fileName, successCount), { id: toastId });

        // Llamar callback de completado
        const callback = completionCallbacks.current.get(uploadId);
        if (callback) {
          callback(completedUpload);
          completionCallbacks.current.delete(uploadId);
        }

      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';

        // No marcar como error si fue cancelado
        if (errorMessage === 'Upload cancelled') {
          setUploads(prev => prev.filter(u => u.id !== uploadId));
          toast.dismiss(toastId);
        } else {
          toast.error(`Error: ${errorMessage}`, { id: toastId });
          updateUpload(uploadId, {
            status: 'error',
            error: errorMessage,
          });
        }

        completionCallbacks.current.delete(uploadId);
      }
    })();

    return uploadId;
  }, [updateUpload]);

  // Legacy method for backward compatibility
  const startOrbitImagesUpload = useCallback((
    projectId: string,
    projectName: string,
    folderName: string,
    zipFile: File,
    onComplete?: (result: BackgroundUpload) => void
  ): string => {
    return startUpload('orbit-images', projectId, projectName, folderName, zipFile, undefined, onComplete);
  }, [startUpload]);

  const cancelUpload = useCallback((uploadId: string) => {
    const xhr = activeXHRs.current.get(uploadId);
    if (xhr) {
      xhr.abort();
      activeXHRs.current.delete(uploadId);
    }
    setUploads(prev => prev.filter(u => u.id !== uploadId));
    completionCallbacks.current.delete(uploadId);
  }, []);

  const removeUpload = useCallback((uploadId: string) => {
    setUploads(prev => prev.filter(u => u.id !== uploadId));
  }, []);

  const clearCompleted = useCallback(() => {
    setUploads(prev => prev.filter(u => u.status !== 'completed' && u.status !== 'error'));
  }, []);

  const activeCount = uploads.filter(u => u.status === 'uploading' || u.status === 'processing').length;
  const hasActiveUploads = activeCount > 0;

  const value: BackgroundUploadContextData = {
    uploads,
    activeCount,
    startUpload,
    startOrbitImagesUpload,
    cancelUpload,
    removeUpload,
    clearCompleted,
    hasActiveUploads,
    isNotificationPanelOpen,
    setNotificationPanelOpen,
  };

  return (
    <BackgroundUploadContext.Provider value={value}>
      {children}
    </BackgroundUploadContext.Provider>
  );
}

export function useBackgroundUpload(): BackgroundUploadContextData {
  const context = useContext(BackgroundUploadContext);

  if (!context) {
    throw new Error('useBackgroundUpload debe usarse dentro de BackgroundUploadProvider');
  }

  return context;
}
